<template>
  <modal
    :show.sync="show"
    body-classes="p-0"
    :modal-classes="`modal-xl ${$style.modalClasses}`">
    <card class="card-form mb-0">
      <template slot="header">
        <h2 class="modal-title">Update Order{{ jobName ? ` - ${jobName}` : '' }}</h2>
      </template>
      <el-divider content-position="left">Details</el-divider>
      <ValidationObserver v-slot="{ invalid, validate }">
        <form
          class="mt-3"
          @submit.prevent="validate().then(updateOrder)"
          autocomplete="off">
          <div class="row">
            <div class="col-4">
              <ValidationProvider
                name="Order No."
                rules="required"
                v-slot="{ errors }">
                <base-input
                  name="orderNo"
                  type="text"
                  label="Order No."
                  v-model="order.orderNumber"
                  :error="errors[0]" />
              </ValidationProvider>
            </div>
            <div class="col-4">
              <base-input
                name="po"
                type="text"
                label="PO."
                v-model="order.purchaseOrder" />
            </div>
            <div class="col-4">
              <ValidationProvider
                name="Status"
                rules="required"
                v-slot="{ errors }">
                <label>Status</label>
                <div class="form-group">
                  <el-select
                    class="select-primary"
                    v-model="order.status"
                    placeholder="Select status">
                    <el-option
                      class="select-primary"
                      v-for="option in statusOptions"
                      :key="option"
                      :label="option"
                      :value="option" />
                  </el-select>
                  <label class="error">{{ errors[0] }}</label>
                </div>
              </ValidationProvider>
            </div>
          </div>
          <div class="mt-5">
            <el-divider content-position="left">Items</el-divider>
            <el-table
              max-height="350"
              :data="order.items"
              @row-click="itemClicked"
              empty-text="No items have been added yet.">
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :width="column.width"
                :min-width="column.minWidth"
                :prop="column.prop"
                :label="column.label"
                :formatter="column.formatter"
                :show-overflow-tooltip="true" />
              <el-table-column
                min-width="100"
                header-align="right"
                label="Actions">
                <div
                  slot-scope="{ $index }"
                  class="text-right">
                  <el-tooltip
                    content="Remove item"
                    :open-delay="800"
                    placement="top">
                    <base-button
                      type="primary"
                      size="sm"
                      icon
                      @click="(event) => { removeItem($index, event) }">
                      <i class="fa fa-trash-alt" />
                    </base-button>
                  </el-tooltip>
                </div>
              </el-table-column>
            </el-table>
            <form
              ref="addItem"
              class="mt-3 no-form-control"
              @submit.prevent="addItem"
              autocomplete="off">
              <div class="row">
                <div
                  class="col-12 flex-between"
                  :class="$style.addItemSection">
                  <el-tooltip
                    manual
                    effect="light"
                    :value="missingName">
                    <base-input
                      name="name"
                      type="text"
                      label="Name"
                      v-model="item.name" />
                    <label
                      slot="content"
                      class="error">Please enter a name.</label>
                  </el-tooltip>
                  <base-input
                    name="Sku"
                    type="text"
                    label="Sku"
                    v-model="item.sku" />
                  <div>
                    <label class="d-block">Quantity</label>
                    <el-input-number
                      v-model="item.qty"
                      :min="1" />
                  </div>
                  <div>
                    <label class="d-block">Status</label>
                    <el-tooltip
                      manual
                      effect="light"
                      :value="missingStatus">
                      <el-select
                        class="select-primary"
                        v-model="item.status"
                        placeholder="Select status">
                        <el-option
                          class="select-primary"
                          v-for="option in detailStatusOptions"
                          :key="option"
                          :label="option"
                          :value="option" />
                      </el-select>
                      <label
                        slot="content"
                        class="error">Please select a status.</label>
                    </el-tooltip>
                  </div>
                  <div class="d-flex">
                    <base-button
                      :class="$style.addItemButton"
                      id="addItem"
                      native-type="submit"
                      type="info"
                      class="btn-fill">
                      Add Item
                    </base-button>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <el-divider />
          <div :class="$style.actions">
            <base-button
              type="default"
              class="btn-fill"
              @click="$emit('update:show', false)">
              Cancel
            </base-button>
            <div :class="$style.right">
              <base-button
                id="updateOrder"
                :disabled="invalid"
                native-type="submit"
                type="primary"
                class="btn-fill">
                Done
              </base-button>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </card>
    <edit-item
      v-if="showEditItemModal"
      :show="showEditItemModal"
      :item="selectedItem"
      @update:show="showEditItemModal = $event;"
      @updateItem="updateItem" />
  </modal>
</template>

<script>
import Vue from 'vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Modal } from 'src/components/UI';
import {
  Divider,
  InputNumber,
} from 'element-ui';
import EditItem from 'src/modules/Manifest/EditItem';

export default {
  name: 'EditOrder',
  components: {
    Modal,
    [Divider.name]: Divider,
    [InputNumber.name]: InputNumber,
    ValidationObserver,
    ValidationProvider,
    EditItem,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    jobName: {
      type: String,
      default: null,
    },
    order: {
      type: Object,
      required: true,
    },
    statusOptions: {
      type: Array,
      required: true,
    },
    detailStatusOptions: {
      type: Array,
      required: true,
    },
  },
  created() {
    this.item = { ...this.emptyItem };
  },
  data() {
    return {
      item: {},
      missingName: false,
      missingStatus: false,
      emptyOrder: {
        items: [],
      },
      emptyItem: {
        qty: 1,
      },
      showEditItemModal: false,
      selectedItem: null,
      tableColumns: [
        {
          prop: '',
          label: 'No.',
          formatter: (_row, _column, _value, index) => index + 1,
        },
        {
          prop: 'name',
          label: 'Name',
        },
        {
          prop: 'sku',
          label: 'SKU',
        },
        {
          prop: 'qty',
          label: 'Qty',
        },
        {
          prop: 'status',
          label: 'Status',
        },
      ],
    };
  },
  methods: {
    noop() {},
    removeItem(index, event) {
      event.stopPropagation();
      const deletedItem = this.order.items.splice(index, 1)[0];
      if (!this.order.deletedItems) {
        this.order.deletedItems = [];
      }
      this.order.deletedItems.push(deletedItem.id);
    },
    validateItem() {
      return new Promise((resolve, reject) => {
        if (!this.item.name) {
          this.missingName = true;
          reject();
        }
        if (!this.item.status) {
          this.missingStatus = true;
          reject();
        }
        resolve();
      });
    },
    itemClicked(item) {
      this.selectedItem = { ...item };
      this.showEditItemModal = true;
    },
    async addItem() {
      try {
        await this.validateItem();
        this.order.items.push(this.item);
        this.item = { ...this.emptyItem };
      } catch (error) {
        this.noop();
      }
    },
    updateOrder() {
      this.$emit('updateOrder', this.order);
    },
    updateItem(item) {
      const index = this.order.items.findIndex(i => i.name === item.name);
      Vue.set(this.order.items, index, item);
    },
  },
  watch: {
    item(val) {
      if (val.name) {
        this.missingName = false;
      }
      if (val.status) {
        this.missingStatus = false;
      }
    },
  },
};
</script>

<style module lang="scss">
@import 'assets/sass/dashboard/custom/_variables.scss';
.modalClasses {
  min-width: 750px;
}
.actions {
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
}
.actions .right > *{
  margin-right: 10px;
  margin-left: 10px;
}
.sectionTitle {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}
.addItemButton {
  align-self: flex-end;
  margin: 0px;
  width: 172px;
}

.addItemSection > * {
  padding-right: 12px;
}
</style>

<style>
.el-select-dropdown__item {
  height: auto!important;
}
.el-tooltip__popper label {
  margin-bottom: 0px;
}
.no-form-control .form-control {
  margin-bottom: 0px!important;
}

.no-form-control .form-group {
  margin-bottom: 0px!important;
}
</style>
